import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import {
  Coins,
  CreditCard,
  History,
  TrendingUp,
  Sparkles,
  ChevronRight,
  CheckCircle2,
  Clock,
  Zap,
  Gift,
  Loader2,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Settings,
  Bell
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Credits() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [usage, setUsage] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [verifying, setVerifying] = useState(false);
  
  // Auto top-off state
  const [autoTopoff, setAutoTopoff] = useState(null);
  const [savingTopoff, setSavingTopoff] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check for successful purchase redirect
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPurchase(sessionId);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [balanceRes, packagesRes, historyRes, usageRes, topoffRes] = await Promise.all([
        axios.get(`${API}/api/credits/balance`),
        axios.get(`${API}/api/credits/packages`),
        axios.get(`${API}/api/credits/history?limit=20`),
        axios.get(`${API}/api/credits/usage?days=30`),
        axios.get(`${API}/api/credits/auto-topoff`)
      ]);
      
      setBalance(balanceRes.data);
      setPackages(packagesRes.data.packages);
      setHistory(historyRes.data.transactions);
      setUsage(usageRes.data);
      setAutoTopoff(topoffRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      toast.error('Failed to load credits data');
    } finally {
      setLoading(false);
    }
  };

  const updateAutoTopoff = async (updates) => {
    setSavingTopoff(true);
    try {
      const res = await axios.put(`${API}/api/credits/auto-topoff`, updates);
      if (res.data.success) {
        setAutoTopoff(prev => ({ ...prev, settings: res.data.settings }));
        toast.success('Auto top-off settings updated');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSavingTopoff(false);
    }
  };

  const toggleAutoTopoff = async (enabled) => {
    await updateAutoTopoff({ enabled });
  };

  const verifyPurchase = async (sessionId) => {
    setVerifying(true);
    try {
      const res = await axios.get(`${API}/api/credits/purchase/verify/${sessionId}`);
      
      if (res.data.status === 'completed') {
        toast.success(`${res.data.credits_added} credits added to your account!`);
        // Clear the URL params
        window.history.replaceState({}, '', '/credits');
        // Refresh data
        fetchData();
      } else if (res.data.status === 'pending') {
        toast.info('Payment is still processing. Please wait a moment.');
      }
    } catch (err) {
      toast.error('Failed to verify purchase');
    } finally {
      setVerifying(false);
    }
  };

  const purchasePackage = async (pkg) => {
    setPurchasing(pkg.id);
    try {
      const res = await axios.post(`${API}/api/credits/purchase`, {
        package_id: pkg.id,
        success_url: `${window.location.origin}/credits/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/credits`
      });
      
      // Redirect to Stripe checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const usagePercent = balance?.monthly_allocation > 0 
    ? ((balance.monthly_allocation - balance.free_credits_remaining) / balance.monthly_allocation) * 100
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 pb-24">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Coins className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">AI Credits</h1>
                <p className="text-xs text-slate-400">Power your AI features</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchData}
              className="border-slate-700 text-slate-300"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Verification Banner */}
      {verifying && (
        <div className="bg-amber-500/20 border-b border-amber-500/30 p-3">
          <div className="max-w-6xl mx-auto flex items-center gap-2 text-amber-300">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Verifying your purchase...</span>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Balance Card */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="md:col-span-2 bg-gradient-to-br from-amber-500/20 to-orange-500/10 border-amber-500/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm mb-1">Available Credits</p>
                  <p className="text-5xl font-bold text-white mb-2" data-testid="credit-balance">
                    {balance?.balance?.toLocaleString() || 0}
                  </p>
                  <div className="flex gap-4 text-sm">
                    <span className="text-slate-400">
                      <Gift className="w-4 h-4 inline mr-1 text-green-400" />
                      Free: <span className="text-white">{balance?.free_credits_remaining || 0}</span>
                    </span>
                    <span className="text-slate-400">
                      <CreditCard className="w-4 h-4 inline mr-1 text-blue-400" />
                      Purchased: <span className="text-white">{balance?.purchased_credits || 0}</span>
                    </span>
                  </div>
                </div>
                <Coins className="w-16 h-16 text-amber-400/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-6">
              <p className="text-slate-400 text-sm mb-2">Monthly Free Credits</p>
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold text-white">
                  {balance?.monthly_allocation || 0}
                </span>
                <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                  {balance?.subscription_tier || 'basic'}
                </Badge>
              </div>
              <Progress value={usagePercent} className="h-2 mb-2" />
              <p className="text-xs text-slate-500">
                {balance?.free_credits_remaining || 0} remaining • Resets {balance?.next_reset ? new Date(balance.next_reset).toLocaleDateString() : 'monthly'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Low Credits Warning */}
        {balance?.balance < 10 && (
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <div className="flex-1">
                <p className="text-white font-medium">Low Credits</p>
                <p className="text-sm text-slate-400">You have less than 10 credits remaining. Purchase more to continue using AI features.</p>
              </div>
              <Button 
                onClick={() => document.getElementById('packages-section').scrollIntoView({ behavior: 'smooth' })}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Buy Credits
              </Button>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="packages" className="space-y-4">
          <TabsList className="bg-slate-900/50 border border-slate-800">
            <TabsTrigger value="packages" className="data-[state=active]:bg-amber-500/20">
              <CreditCard className="w-4 h-4 mr-2" />
              Buy Credits
            </TabsTrigger>
            <TabsTrigger value="auto-topoff" className="data-[state=active]:bg-amber-500/20">
              <Bell className="w-4 h-4 mr-2" />
              Auto Top-Off
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-amber-500/20">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
            <TabsTrigger value="usage" className="data-[state=active]:bg-amber-500/20">
              <TrendingUp className="w-4 h-4 mr-2" />
              Usage
            </TabsTrigger>
          </TabsList>

          {/* Packages Tab */}
          <TabsContent value="packages" id="packages-section">
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {packages.map((pkg) => (
                <Card 
                  key={pkg.id}
                  className={`relative overflow-hidden transition-all hover:scale-[1.02] ${
                    pkg.popular 
                      ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/10 border-amber-500/50' 
                      : pkg.bonus
                      ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-500/50'
                      : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                  }`}
                  data-testid={`package-${pkg.id}`}
                >
                  {pkg.popular && (
                    <div className="absolute top-0 right-0 bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded-bl">
                      POPULAR
                    </div>
                  )}
                  {pkg.bonus && !pkg.popular && (
                    <div className="absolute top-0 right-0 bg-green-500 text-black text-xs font-bold px-2 py-1 rounded-bl">
                      BONUS
                    </div>
                  )}
                  <CardContent className="p-5">
                    <div className="mb-4">
                      <p className="text-3xl font-bold text-white">{pkg.credits.toLocaleString()}</p>
                      <p className="text-slate-400 text-sm">credits</p>
                    </div>
                    
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-2xl font-bold text-white">${pkg.price.toFixed(2)}</span>
                    </div>
                    
                    {pkg.savings && (
                      <Badge className="bg-green-500/20 text-green-400 border-0 mb-3 text-xs">
                        {pkg.savings}
                      </Badge>
                    )}
                    
                    <p className="text-xs text-slate-500 mb-4">
                      ${pkg.price_per_credit?.toFixed(2) || (pkg.price / pkg.credits).toFixed(2)}/credit
                    </p>
                    
                    <Button 
                      onClick={() => purchasePackage(pkg)}
                      disabled={purchasing === pkg.id}
                      className={`w-full ${
                        pkg.popular 
                          ? 'bg-amber-600 hover:bg-amber-700' 
                          : pkg.bonus
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-slate-700 hover:bg-slate-600'
                      }`}
                    >
                      {purchasing === pkg.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Purchase
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="mt-6 bg-slate-900/50 border-slate-800">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  How Credits Work
                </h3>
                <div className="grid md:grid-cols-3 gap-6 text-sm">
                  <div>
                    <p className="text-slate-300 font-medium mb-1">Token-Based Billing</p>
                    <p className="text-slate-500">Credits are calculated based on actual AI token usage. Longer content uses more credits.</p>
                  </div>
                  <div>
                    <p className="text-slate-300 font-medium mb-1">Never Expire</p>
                    <p className="text-slate-500">Purchased credits never expire. Free monthly credits reset on the 1st of each month.</p>
                  </div>
                  <div>
                    <p className="text-slate-300 font-medium mb-1">Transparent Pricing</p>
                    <p className="text-slate-500">See exactly how many credits each operation uses before and after generation.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Auto Top-Off Tab */}
          <TabsContent value="auto-topoff">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Settings Card */}
              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-400" />
                    Auto Top-Off Settings
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Automatically purchase credits when your balance gets low
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-white font-medium">Enable Auto Top-Off</p>
                      <p className="text-slate-400 text-sm">
                        {autoTopoff?.settings?.enabled 
                          ? 'Credits will be purchased automatically' 
                          : 'Credits are purchased manually as needed'}
                      </p>
                    </div>
                    <Switch
                      checked={autoTopoff?.settings?.enabled || false}
                      onCheckedChange={toggleAutoTopoff}
                      disabled={savingTopoff}
                    />
                  </div>

                  {/* Threshold Setting */}
                  <div className="space-y-2">
                    <label className="text-slate-300 text-sm font-medium">
                      Trigger Threshold
                    </label>
                    <p className="text-slate-500 text-xs mb-2">
                      Auto top-off when balance falls below this amount
                    </p>
                    <div className="flex gap-2">
                      {[5, 10, 20, 50].map(threshold => (
                        <Button
                          key={threshold}
                          variant={autoTopoff?.settings?.threshold === threshold ? "default" : "outline"}
                          size="sm"
                          onClick={() => updateAutoTopoff({ threshold })}
                          disabled={savingTopoff}
                          className={autoTopoff?.settings?.threshold === threshold 
                            ? "bg-amber-600 hover:bg-amber-700" 
                            : "border-slate-700 text-slate-300"}
                        >
                          {threshold} credits
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Package Size */}
                  <div className="space-y-2">
                    <label className="text-slate-300 text-sm font-medium">
                      Top-Off Package
                    </label>
                    <p className="text-slate-500 text-xs mb-2">
                      How many credits to purchase each time
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {autoTopoff?.available_packages && Object.entries(autoTopoff.available_packages).map(([size, pkg]) => (
                        <Button
                          key={size}
                          variant={autoTopoff?.settings?.package_size === size ? "default" : "outline"}
                          size="sm"
                          onClick={() => updateAutoTopoff({ package_size: size })}
                          disabled={savingTopoff}
                          className={`flex-col h-auto py-3 ${
                            autoTopoff?.settings?.package_size === size 
                              ? "bg-amber-600 hover:bg-amber-700" 
                              : "border-slate-700 text-slate-300"
                          }`}
                        >
                          <span className="font-bold capitalize">{size}</span>
                          <span className="text-xs opacity-70">{pkg.credits} credits</span>
                          <span className="text-xs opacity-70">${pkg.price}</span>
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Monthly Limit */}
                  <div className="space-y-2">
                    <label className="text-slate-300 text-sm font-medium">
                      Monthly Limit
                    </label>
                    <p className="text-slate-500 text-xs mb-2">
                      Maximum auto top-offs per month (safety limit)
                    </p>
                    <div className="flex gap-2">
                      {[1, 3, 5, 10].map(limit => (
                        <Button
                          key={limit}
                          variant={autoTopoff?.settings?.max_monthly_topoffs === limit ? "default" : "outline"}
                          size="sm"
                          onClick={() => updateAutoTopoff({ max_monthly_topoffs: limit })}
                          disabled={savingTopoff}
                          className={autoTopoff?.settings?.max_monthly_topoffs === limit 
                            ? "bg-amber-600 hover:bg-amber-700" 
                            : "border-slate-700 text-slate-300"}
                        >
                          {limit}x/month
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Status Card */}
              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Current Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-white">
                        {autoTopoff?.settings?.enabled ? (
                          <span className="text-green-400">Active</span>
                        ) : (
                          <span className="text-slate-500">Disabled</span>
                        )}
                      </p>
                      <p className="text-slate-400 text-sm">Status</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-white">
                        {autoTopoff?.settings?.topoffs_this_month || 0} / {autoTopoff?.settings?.max_monthly_topoffs || 5}
                      </p>
                      <p className="text-slate-400 text-sm">Used This Month</p>
                    </div>
                  </div>

                  {autoTopoff?.settings?.enabled && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                      <h4 className="text-amber-400 font-medium mb-2">Auto Top-Off Active</h4>
                      <p className="text-slate-300 text-sm">
                        When your balance falls below <strong>{autoTopoff?.settings?.threshold} credits</strong>, 
                        we'll automatically purchase <strong>{autoTopoff?.package_details?.credits} credits</strong> for 
                        <strong> ${autoTopoff?.package_details?.price}</strong>.
                      </p>
                    </div>
                  )}

                  {!autoTopoff?.settings?.enabled && (
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h4 className="text-slate-300 font-medium mb-2">Manual Mode</h4>
                      <p className="text-slate-400 text-sm">
                        Credits are purchased manually. Enable auto top-off to never run out of credits during important tasks.
                      </p>
                    </div>
                  )}

                  {autoTopoff?.settings?.last_topoff_at && (
                    <div className="text-slate-500 text-xs">
                      Last auto top-off: {new Date(autoTopoff.settings.last_topoff_at).toLocaleString()}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-white">Transaction History</CardTitle>
                <CardDescription className="text-slate-400">Your recent credit transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((txn, idx) => (
                      <div 
                        key={txn.transaction_id || idx}
                        className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            txn.credits > 0 
                              ? 'bg-green-500/20' 
                              : txn.type === 'usage' 
                              ? 'bg-violet-500/20' 
                              : 'bg-slate-700'
                          }`}>
                            {txn.credits > 0 ? (
                              <CreditCard className="w-5 h-5 text-green-400" />
                            ) : txn.type === 'usage' ? (
                              <Sparkles className="w-5 h-5 text-violet-400" />
                            ) : (
                              <Clock className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{txn.description}</p>
                            <p className="text-slate-500 text-xs">
                              {new Date(txn.created_at).toLocaleString()}
                              {txn.tokens > 0 && ` • ${txn.tokens.toLocaleString()} tokens`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${
                            txn.credits > 0 ? 'text-green-400' : 'text-slate-300'
                          }`}>
                            {txn.credits > 0 ? '+' : ''}{txn.credits}
                          </p>
                          <p className="text-slate-500 text-xs">
                            Balance: {txn.balance_after}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Usage Tab */}
          <TabsContent value="usage">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Usage by Operation</CardTitle>
                  <CardDescription className="text-slate-400">Last 30 days</CardDescription>
                </CardHeader>
                <CardContent>
                  {usage?.by_operation && Object.keys(usage.by_operation).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(usage.by_operation).map(([operation, data]) => (
                        <div key={operation} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                          <div>
                            <p className="text-white text-sm font-medium capitalize">
                              {operation.replace('_', ' ')}
                            </p>
                            <p className="text-slate-500 text-xs">
                              {data.count} operations • {data.total_tokens?.toLocaleString() || 0} tokens
                            </p>
                          </div>
                          <Badge variant="outline" className="border-violet-500/50 text-violet-400">
                            {data.total_credits} credits
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No usage data yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Summary</CardTitle>
                  <CardDescription className="text-slate-400">Last {usage?.period_days || 30} days</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-white">{usage?.total_credits_used || 0}</p>
                      <p className="text-slate-400 text-sm">Credits Used</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-white">{(usage?.total_tokens_used || 0).toLocaleString()}</p>
                      <p className="text-slate-400 text-sm">Tokens Used</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-white">{balance?.total_used || 0}</p>
                      <p className="text-slate-400 text-sm">Lifetime Credits</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-white">
                        ${((usage?.total_credits_used || 0) * 0.012).toFixed(2)}
                      </p>
                      <p className="text-slate-400 text-sm">Value Used</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Quick Actions */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 flex flex-wrap gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate('/ai-assistant')}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <Sparkles className="w-4 h-4 mr-2 text-violet-400" />
              Use AI Assistant
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/pricing')}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <ChevronRight className="w-4 h-4 mr-2" />
              View Subscription Plans
            </Button>
          </CardContent>
        </Card>
      </div>

      <Footer />
      <FloatingDock />
    </div>
  );
}
